// ============================================================================
// src/cattle/CattleEntry.tsx — the cattle domain's nested routes.
// ============================================================================
// B3.1 finish. This is the shape the spec asks for:
//
//     App.tsx      AppBar + top-level routes, one per domain
//     CattleEntry  nested routes for that domain
//
// It replaces resolveCattleEntry, the fall-through resolver B3.2 moved here.
// The resolver existed because a `view === 'X'` chain has to be able to say
// "not mine, keep looking", which a component cannot express. A route table
// can: App asks routedLocationFor() whether this domain claims the current
// view, and only renders <CattleEntry> when it does. <AppRoutes> then picks
// the one route that matches, and the fall-through disappears with the chain
// that needed it.
//
// The `location` prop is the ROUTED location, not the raw one — see
// src/app/navigation/viewRoutes.js for why the two differ for exactly one
// frame per view-driven navigation, and why matching the raw location there
// would mount the wrong view.
// ============================================================================
import React from 'react';

import {AppRoute, AppRoutes} from '../app/navigation/index.js';
import {CATTLE_ROUTES} from './cattleRoutes.js';

import CattleDailysView from './CattleDailysView.tsx';
import CattleHomeView from './CattleHomeView.tsx';
import CattleHerdsView from './CattleHerdsView.tsx';
import CattleBreedingView from './CattleBreedingView.tsx';
import CattleForecastView from './CattleForecastView.tsx';
import CattleBatchesView from './CattleBatchesView.tsx';
import CattleLogPage from './CattleLogPage.tsx';
import CattleWeighInsView from './CattleWeighInsView.tsx';
import PageBoundary from '../app/PageBoundary.tsx';

export {CATTLE_ROUTES};

// What routedLocationFor() hands back once App has confirmed this domain owns
// the view: either the real driver location object or, during the one frame
// where the URL has not caught up, the view's canonical path as a bare string.
// Both are shapes <AppRoutes> accepts; only `pathname` is ever matched on.
export type RoutedLocationProp = string | {pathname: string; search?: string; hash?: string; state?: unknown};

type CattleEntryProps = {
  location: RoutedLocationProp;
};

export function CattleEntry({location}: CattleEntryProps) {
  const routes = (
    <AppRoutes location={location}>
      <AppRoute path="/cattle/dailys/*" element={<CattleDailysView />} />
      <AppRoute path="/cattle" element={<CattleHomeView />} />
      <AppRoute path="/cattle/herds/*" element={<CattleHerdsView />} />
      <AppRoute path="/cattle/breeding" element={<CattleBreedingView />} />
      <AppRoute path="/cattle/forecast" element={<CattleForecastView />} />
      <AppRoute path="/cattle/batches/*" element={<CattleBatchesView />} />
      {/* Cattle Log — singleton field log at /cattle/log. Reachable by every
          authenticated role except equipment_tech/inactive; Light reaches it via
          the LIGHT_ALLOWED_VIEWS carve-out (same pattern as cattledailys). */}
      <AppRoute path="/cattle/log" element={<CattleLogPage />} />
      <AppRoute path="/cattle/weighins" element={<CattleWeighInsView />} />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default CattleEntry;
